import { Component, EventEmitter, inject, Output } from '@angular/core';
import * as XLSX from 'xlsx';
import { LocalCsvService } from '../../../../../services/local-csv/local-csv.service';
import {
  SPREADSHEET_ACCEPT,
  getFileExtension,
  isSpreadsheetFile,
} from '../../../../../../utils/utils';

import { stripTextMarker } from '../../../../../../utils/csv';

/** A spreadsheet cell as text, the way a CSV gives it: Excel keeps typed phone numbers as numbers. */
function cellText(cell: unknown): string {
  return cell === null || cell === undefined ? '' : String(cell);
}

@Component({
  selector: 'upload-csv-phone-number-file',
  standalone: true,
  imports: [],
  templateUrl: './upload-csv-file.component.html',
  styleUrl: './upload-csv-file.component.css',
})
export class UploadCsvFileComponent {
  constructor() {}

  @Output() goToDataPreviewPage = new EventEmitter();

  localCSVService = inject(LocalCsvService);

  fileName: string = '';
  loading: boolean = false;
  tableHeaders: string[] = [];
  tableData: string[][] = [];
  isHovering: boolean = false;
  errorMessage: string = '';
  readonly MAX_CONTACTS = 70000;
  readonly acceptedFormats = SPREADSHEET_ACCEPT;

  private setError(message: string) {
    this.errorMessage = message;
    this.tableData = [];
    this.tableHeaders = [];
    this.loading = false;
  }

  private clearError() {
    this.errorMessage = '';
  }

  private checkContactLimit(data: string[][]): boolean {
    if (data.length > this.MAX_CONTACTS) {
      this.setError(
        `This file contains ${data.length.toLocaleString()} rows, which exceeds the ${this.MAX_CONTACTS.toLocaleString()}-contact limit. Please split the file and try again.`
      );
      return false;
    }
    return true;
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isHovering = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isHovering = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isHovering = false;
    if (event.dataTransfer?.files) {
      const file = event.dataTransfer.files[0];
      this.processFile(file);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.processFile(input.files[0]);
      // Clear the picker so choosing the same file again (after fixing it) is read again.
      input.value = '';
    }
  }

  private processFile(file: File) {
    this.clearError();
    this.fileName = file.name;
    this.localCSVService.updateFileName(file.name);
    this.localCSVService.updateFileSize(file.size);
    this.loading = true;
    const fileExtension = getFileExtension(file.name);

    if (!isSpreadsheetFile(file.name)) {
      this.setError(
        `Unsupported file format ".${fileExtension || 'unknown'}". Please upload a CSV or spreadsheet file.`
      );
      this.fileName = '';
      return;
    }

    // Plain CSV streams through the chunked reader (handles very large files up
    // to the contact limit); every other spreadsheet format is parsed by xlsx.
    if (fileExtension === 'csv') {
      this.parseLargeCsvFile(file);
    } else {
      this.parseExcelFile(file);
    }
  }

  parseLargeCsvFile(file: File): void {
    const reader = new FileReader();
    const CHUNK_SIZE = 1024 * 1024; // 1 MB chunk size
    let offset = 0;
    let isFirstChunk = true;
    let remainingText = '';

    reader.onload = (e) => {
      try {
        const chunk = e.target?.result as string;
        const text = remainingText + chunk;
        const lines = text.split('\n');

        // Keep the last line as it might be incomplete
        remainingText = lines.pop() || '';

        if (isFirstChunk && lines.length > 0) {
          this.tableHeaders = lines[0]
            .split(',')
            .map((header) => header.trim().replace(/\r$/, ''));
          this.tableData = lines
            .slice(1)
            .map((row) =>
              row.split(',').map((cell) => stripTextMarker(cell.trim().replace(/\r$/, '')))
            )
            .filter((row) => row.some((cell) => cell !== ''));

          if (!this.checkContactLimit(this.tableData)) {
            return;
          }

          isFirstChunk = false;
        } else if (!isFirstChunk) {
          const newData = lines
            .map((row) =>
              row.split(',').map((cell) => stripTextMarker(cell.trim().replace(/\r$/, '')))
            )
            .filter((row) => row.some((cell) => cell !== ''));

          const combinedData = [...this.tableData, ...newData];
          if (!this.checkContactLimit(combinedData)) {
            return;
          }

          this.tableData = combinedData;
        }

        offset += CHUNK_SIZE;

        if (offset < file.size) {
          this.readNextChunk(reader, file, offset);
        } else {
          // Process any remaining text
          if (remainingText) {
            const lastRow = remainingText
              .split(',')
              .map((cell) => stripTextMarker(cell.trim().replace(/\r$/, '')));
            if (
              lastRow.length === this.tableHeaders.length &&
              lastRow.some((cell) => cell !== '')
            ) {
              const newData = [...this.tableData, lastRow];
              if (!this.checkContactLimit(newData)) {
                return;
              }
              this.tableData = newData;
            }
          }

          if (this.tableHeaders.length === 0) {
            this.setError('The file appears to be empty or could not be read.');
            return;
          }
          if (this.tableData.length === 0) {
            this.setError(
              'The file has no data rows. Please check the file and try again.'
            );
            return;
          }

          this.loading = false;
        }
      } catch (error) {
        this.setError(
          'Failed to parse the CSV file. Please check that the file is not corrupted and try again.'
        );
      }
    };

    reader.onerror = () => {
      this.setError(
        'Could not read the file. Please check your file and try again.'
      );
    };

    this.readNextChunk(reader, file, offset);
  }

  readNextChunk(reader: FileReader, file: File, offset: number): void {
    const blob = file.slice(offset, offset + 1024 * 1024);
    reader.readAsText(blob);
  }

  parseExcelFile(file: File): void {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        // defval fills empty cells, so every row is as long as the sheet is wide.
        const jsonData = XLSX.utils
          .sheet_to_json<unknown[]>(worksheet, { header: 1, defval: '' })
          .map((row) => row.map(cellText));

        if (!jsonData || jsonData.length === 0) {
          this.setError(
            'The file appears to be empty or could not be read.'
          );
          return;
        }

        this.tableHeaders = jsonData[0].map((header) => header.trim());
        this.tableData = jsonData
          .slice(1)
          .map((row) => row.map((cell) => stripTextMarker(cell)))
          .filter((row) => row.some((cell) => cell !== ''));

        if (!this.checkContactLimit(this.tableData)) {
          return;
        }

        if (this.tableData.length === 0) {
          this.setError(
            'The file has no data rows. Please check the file and try again.'
          );
          return;
        }

        this.loading = false;
      } catch {
        this.setError(
          'Failed to parse the Excel file. Please check that the file is not corrupted and try again.'
        );
      }
    };

    reader.onerror = () => {
      this.setError(
        'Could not read the file. Please check your file and try again.'
      );
    };

    reader.readAsArrayBuffer(file);
  }

  continue() {
    this.localCSVService.updateTableHeaders(this.tableHeaders);
    this.localCSVService.updateTableData(this.tableData);
    this.goToDataPreviewPage.emit();
  }
}
